import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ScrollDetail } from "@ionic/core/components";
import { useScrollPositionStore } from "../providers/scrollPosition";

/**
 * Hook for saving and restoring scroll position when navigating away and back to a page
 * @param pageKey - Unique key to identify the page
 * @param isDataLoaded - Optional flag to indicate when data is loaded (to restore scroll after data loads)
 * @returns object with contentRef and handleScroll callback
 */
export const useScrollRestoration = (pageKey: string, isDataLoaded: boolean = true) => {
  const contentRef = useRef<HTMLIonContentElement>(null);
  const { savePosition, getPosition } = useScrollPositionStore();
  const hasRestoredRef = useRef(false);
  const [shouldRestore, setShouldRestore] = useState(false);

  // Check if we have saved position on mount
  useLayoutEffect(() => {
    const savedPosition = getPosition(pageKey);
    if (savedPosition > 0 && !hasRestoredRef.current) {
      setShouldRestore(true);
    }
  }, [pageKey, getPosition]);

  // Restore scroll position when data is loaded
  useEffect(() => {
    if (!shouldRestore || !isDataLoaded || hasRestoredRef.current) return;

    const restoreScroll = () => {
      if (!contentRef.current) return;

      const savedPosition = getPosition(pageKey);

      if (savedPosition > 0) {
        // Use requestAnimationFrame to sync with browser paint
        requestAnimationFrame(() => {
          requestAnimationFrame(async () => {
            try {
              if (contentRef.current) {
                // Use duration 0 for instant scroll without animation
                await contentRef.current.scrollToPoint(0, savedPosition, 0);
                hasRestoredRef.current = true;
                setShouldRestore(false);
              }
            } catch (error) {
              console.error("Error restoring scroll:", error);
            }
          });
        });
      }
    };

    restoreScroll();
  }, [pageKey, getPosition, shouldRestore, isDataLoaded]);

  // Handler for scroll events
  const handleScroll = useCallback(
    (event: CustomEvent<ScrollDetail>) => {
      const position = event.detail.scrollTop;
      if (position > 0) {
        savePosition(pageKey, position);
      }
    },
    [pageKey, savePosition],
  );

  // Save position when navigating away
  useEffect(() => {
    const currentContent = contentRef.current;

    return () => {
      const saveOnUnmount = async () => {
        if (!currentContent) return;

        try {
          const scrollElement = await currentContent.getScrollElement();
          const position = scrollElement.scrollTop;
          if (position > 0) {
            savePosition(pageKey, position);
          }
        } catch (error) {
          console.error("Error saving scroll on unmount:", error);
        }
      };

      saveOnUnmount();
    };
  }, [pageKey, savePosition]);

  return { contentRef, handleScroll };
};
